import { ThemeColorSet } from '@/config/theme/themeColors';

export const TOP_BAR_HEIGHT = 64;
export const TOP_BAR_Z_INDEX = 1000;

export const microLabelSx = (color: string) => ({
  color,
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.1em',
  lineHeight: 1.2,
  textTransform: 'uppercase' as const,
});

export const numericSx = { fontVariantNumeric: 'tabular-nums' } as const;

export const panelSx = (colors: ThemeColorSet) => ({
  background: colors.bgCard,
  border: `1px solid ${colors.border}`,
  borderRadius: '20px',
  boxShadow: colors.shadowCard,
});

export const innerPanelSx = (colors: ThemeColorSet) => ({
  background: colors.bgCard2,
  border: `1px solid ${colors.border}`,
  borderRadius: '16px',
});

export const sectionLabelSx = (colors: ThemeColorSet) => ({
  '&::after': {
    background: colors.border,
    content: '""',
    flex: 1,
    height: '1px',
  },
  alignItems: 'center',
  color: colors.textMuted,
  display: 'flex',
  fontSize: '11px',
  fontWeight: 800,
  gap: '8px',
  textTransform: 'uppercase' as const,
});

export const filterSelectSx = (colors: ThemeColorSet) => ({
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accentInk },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
  background: colors.bgCard2,
  borderRadius: '10px',
  fontSize: '0.85rem',
});

export const filterSearchFieldSx = (colors: ThemeColorSet) => ({
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': { borderColor: colors.accentInk },
    '& fieldset': { borderColor: colors.border },
    background: colors.bgCard2,
    borderRadius: '10px',
    fontSize: '0.85rem',
  },
});

export const iconTileSx = (background: string, size = 44, borderColor?: string) => ({
  alignItems: 'center',
  background,
  border: borderColor ? `1px solid ${borderColor}` : undefined,
  borderRadius: '12px',
  display: 'flex',
  flexShrink: 0,
  height: size,
  justifyContent: 'center',
  width: size,
});

export const statusPillSx = (color: string) => ({
  background: `${color}1A`,
  border: `1px solid ${color}40`,
  borderRadius: 50,
  color,
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: 1.4,
  px: 1.25,
  py: 0.25,
  whiteSpace: 'nowrap' as const,
});

export const progressBarSx = (colors: ThemeColorSet, from: string, to: string, height = 6) => ({
  '& .MuiLinearProgress-bar': {
    background: `linear-gradient(90deg, ${from}, ${to})`,
    borderRadius: 4,
  },
  background: colors.bgCard3,
  borderRadius: 4,
  height,
});

/**
 * MuiButton defaults to variant="contained", so every button needs an explicit
 * base background — otherwise the theme's accent fill leaks through.
 */
export const primaryButtonSx = (colors: ThemeColorSet) => ({
  '&:hover': { background: colors.accentDark, borderColor: colors.accentDark },
  '&.Mui-disabled': {
    background: colors.bgCard2,
    borderColor: colors.border,
    color: colors.textMuted,
  },
  background: colors.accent,
  borderColor: colors.accent,
  borderRadius: '999px',
  color: colors.black,
  fontWeight: 800,
  textTransform: 'none' as const,
});

export const outlineButtonSx = (colors: ThemeColorSet) => ({
  '&:hover': {
    background: colors.bgHover,
    borderColor: colors.borderStrong,
    boxShadow: 'none',
  },
  '&.Mui-disabled': { borderColor: colors.border, color: colors.textMuted },
  background: 'transparent',
  borderColor: colors.border,
  borderRadius: '999px',
  // The contained variant (the theme default) adds a gold glow; ghosts must clear it
  boxShadow: 'none',
  color: colors.textPrimary,
  fontWeight: 700,
  textTransform: 'none' as const,
});

export const ghostButtonSx = (colors: ThemeColorSet) => ({
  '&:hover': {
    background: colors.bgHover,
    borderColor: 'transparent',
    boxShadow: 'none',
    color: colors.textPrimary,
  },
  '&.Mui-disabled': { color: colors.textMuted },
  background: 'transparent',
  borderColor: 'transparent',
  borderRadius: '999px',
  boxShadow: 'none',
  color: colors.textSecondary,
  fontWeight: 700,
  textTransform: 'none' as const,
});

export const revealSx = (index = 0) => ({
  '@keyframes uiReveal': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  animation: 'uiReveal 460ms cubic-bezier(0.16, 1, 0.3, 1) both',
  animationDelay: `${index * 60}ms`,
});

export const pressableSx = {
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  '&:active:not(.Mui-disabled)': { transform: 'scale(0.96)' },
  transition: 'transform 120ms cubic-bezier(0.2, 0, 0, 1)',
} as const;

export const globalFabClearanceSx = {
  pb: { sm: 12, xs: 10 },
} as const;
